import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { reverseAriaExpanded } from './utils';
import { mapboxgl, createMap } from './mapbox';

// GSAP setup
gsap.registerPlugin(ScrollToPlugin);

// Elements
const container = document.querySelector('.map');
const isBlock = container.classList.contains('is-block');
const canvas = document.querySelector('.map-canvas');
const loader = document.querySelector('.map-loader');
const locationContainer = document.querySelector('.map-locations');
const filter = document.querySelector('.map-filter');
const filterBox = filter?.querySelector('.map-filter-box');
const filterToggle = document.querySelector('.map-filter-toggle');
const filterToggleLabel = filterToggle?.querySelector('.map-filter-toggle-label');
const filterToggleLabelWhenOpen = filterToggleLabel?.dataset.labelWhenOpen;
const filterToggleLabelWhenClosed = filterToggleLabel?.textContent;
const filterGroups = filter?.querySelectorAll('.map-filter-group') ?? [];

// Data container variables
let markers = [];
let currentMarker = null;
let currentCard = null;
let bounds = null;
let filterIsOpen = false;
let filterHasChanged = false;
let currentCategories = [];

// Map setup
const map = createMap({
    container: canvas,
    center: [4.5424, 52.3787],
    zoom: 14
});

// If the map is a block, we disable the scroll zoom and add zoom and rotation controls
// so users can scroll past the map without zooming in or out
if (isBlock) {
    // Disable map zoom on scroll, so users can scroll past the map
    map.scrollZoom.disable();

    // Add zoom and rotation controls to the map
    map.addControl(new mapboxgl.NavigationControl());
}

function getBoundsOptions(extraPaddingBottom = false) {
    if (window.matchMedia('(max-width: 767px)').matches) {
        return {
            padding: { top: 100, right: 30, bottom: (extraPaddingBottom ? 300 : 120), left: 30 }
        }
    } else {
        return {
            padding: { top: 100, right: 100, bottom: 100, left: 424 }
        };
    }
}

function createMarkers(addCategoryIdsToMarkers) {
    const locations = locationContainer.querySelectorAll('.map-location');

    if (!locations.length) {
        return;
    }

    bounds = new mapboxgl.LngLatBounds();
    
    locations.forEach(location => {
        const lng = parseFloat(location.dataset.lng);
        const lat = parseFloat(location.dataset.lat);
        const marker = document.createElement('div');
        
        marker.className = 'map-marker';
        marker.dataset.id = location.dataset.id;
        marker.addEventListener('click', handleMarkerClick);

        if (addCategoryIdsToMarkers) {
            marker.dataset.categoryIds = location.dataset.categoryIds;
        } else { // We assume that the map is not filtered, so we are showing the highlights only
            marker.classList.add('highlight');
        }

        markers.push(new mapboxgl.Marker(marker).setLngLat([lng, lat]).addTo(map));
        bounds.extend([lng, lat]);
    });
    
    if (locations.length > 1) {
        map.fitBounds(bounds, getBoundsOptions());
    } else {
        map.flyTo({ center: bounds.getCenter(), zoom: 14 });
    }
}

function removeMarkers() {
    markers.forEach(marker => marker.remove());
    markers = [];
}

function activateLocation(locationId) {
    const targetMarker = markers.find(marker => marker.getElement().dataset.id == locationId);
    const targetCard = locationContainer.querySelector('.map-location[data-id="' + locationId + '"]');

    if (!targetMarker || !targetCard) {
        return;
    }

    currentMarker?.getElement().classList.remove('active');
    currentCard?.classList.remove('active');

    targetMarker.getElement().classList.add('active');
    targetCard.classList.add('active');

    currentMarker = targetMarker;
    currentCard = targetCard;

    // Side effect 1: highlight the target marker
    map.fitBounds(bounds, getBoundsOptions(true));

    // Side effect 2: scroll the location container to the target card
    const cardContainerHeight = locationContainer.getBoundingClientRect().height;
    const cardHeight = targetCard.getBoundingClientRect().height;
    const maxScroll = locationContainer.scrollHeight - cardContainerHeight;

    let targetPosition = targetCard.offsetTop - (cardContainerHeight / 2) + (cardHeight / 2);

    if (targetPosition < 0) {
        targetPosition = 0;
    } else if (targetPosition > maxScroll) {
        targetPosition = maxScroll;
    }

    gsap.to(locationContainer, { duration: 0.8, scrollTo: targetPosition, ease: 'power3.inOut' });

    // Side effect 3: close the filter
    closeFilter();
}

function deactivateLocation() {
    currentMarker?.getElement().classList.remove('active');
    currentCard?.classList.remove('active');

    currentMarker = currentCard = null;

    if (markers.length > 1) {
        map.fitBounds(bounds, getBoundsOptions());
    }
}

function handleMarkerClick(e) {
    const marker = e.target;
    
    if (currentMarker?.getElement() === marker) {
        deactivateLocation();
    } else {
        activateLocation(marker.dataset.id);
    }
}

function handleLocationContainerClick(e) {
    const isCloseButton = e.target.classList.contains('.card-close') || e.target.closest('.card-close');
    const isShowButton = e.target.classList.contains('.card-show-on-map') || e.target.closest('.card-show-on-map');
    const card = e.target.closest('.map-location');

    if (!isCloseButton && !isShowButton) {
        return;
    }

    e.stopPropagation();
    e.preventDefault();

    if (isShowButton && card !== currentCard) {
        activateLocation(card.dataset.id);
    } else if (isCloseButton || (isShowButton && card === currentCard)) {
        deactivateLocation();
    }
}

function handleFilterChange(e) {
    const checkbox = e.target;
    const group = checkbox.closest('.map-filter-group');
    const parentCheckbox = group.querySelector('.map-filter-group-input');
    const childCheckboxes = group.querySelectorAll('.map-filter-item-input');
    const isParentCheckbox = checkbox === parentCheckbox;
    const allChildrenAreChecked = Array.from(childCheckboxes).every(childCheckbox => childCheckbox.checked);
    const anyChildIsChecked = Array.from(childCheckboxes).some(childCheckbox => childCheckbox.checked);

    if (isParentCheckbox) {
        childCheckboxes.forEach(childCheckbox => {
            childCheckbox.checked = checkbox.checked;
        });
    } else {
        if (allChildrenAreChecked) {
            parentCheckbox.indeterminate = false;
            parentCheckbox.checked = true;
        } else if (anyChildIsChecked) {
            parentCheckbox.indeterminate = true;
            parentCheckbox.checked = false;
        } else {
            parentCheckbox.indeterminate = false;
            parentCheckbox.checked = false;
        }
    }

    currentCategories = [];

    filter.querySelectorAll('.map-filter-item-input:checked').forEach(checkbox => {
        currentCategories.push(checkbox.value);
    });

    filterHasChanged = true;
}

function executeFilter() {
    if (!filterHasChanged) {
        return;
    }

    fetch(urls.ajax, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: 'action=filter_locations&categories=' + currentCategories.join('|')
    }).then(response => response.text()).then(result => {
        locationContainer.innerHTML = result;

        removeMarkers();
        createMarkers(!!currentCategories.length);

        filterHasChanged = false;
    });
}

function closeFilter() {
    if (filterIsOpen) {
        filterToggleLabel.textContent = filterToggleLabelWhenClosed;

        // It would be better to create a timeline outside of this function and reuse it, 
        // but that causes problems with the height animation because the inner panels animate too.
        gsap.timeline()
            .to(filterBox, { duration: 0.5, height: 0, y: -8, opacity: 0, ease: 'power3.inOut' })
            .set(filterBox, { display: 'none' });

        reverseAriaExpanded(filterToggle);
    }

    filterIsOpen = false;
}

function openFilter() {
    if (!filterIsOpen) {
        filterToggleLabel.textContent = filterToggleLabelWhenOpen;

        gsap.timeline()
            .set(filterBox, { display: 'block', y: -8, opacity: 0 })
            .to(filterBox, { duration: 0.5, height: 'auto', y: 0, opacity: 1, ease: 'power3.inOut' });

        reverseAriaExpanded(filterToggle);
    }

    filterIsOpen = true;
}

function handleFilterToggleClick() {
    if (filterIsOpen) {
        executeFilter();
        closeFilter();
    } else {
        openFilter();
    }
}

createMarkers(false);
map.on('load', () => loader.classList.add('hidden'));
locationContainer.addEventListener('click', handleLocationContainerClick);
filter?.addEventListener('change', handleFilterChange);
filterToggle?.addEventListener('click', handleFilterToggleClick);

// Handle filter group toggles
filterGroups.forEach(group => {
    const toggle = group.querySelector('.map-filter-group-toggle');
    const items = group.querySelector('.map-filter-items');
    
    const animation = gsap.timeline({ paused: true })
        .to(items, { duration: 0.5, height: 'auto', ease: 'power3.inOut' });
    
    toggle.addEventListener('click', () => {
        const isOpen = group.classList.contains('open');

        group.classList.toggle('open');

        if (isOpen) {
            animation.reverse();
        } else {
            animation.play();
        }
    });
});